import { ComponentChildren } from 'preact';
import { useState, useRef, useCallback, useEffect } from 'preact/hooks';
import { keyExtractor, getPrevNextKey } from '../utils/PRIVATE_collectionUtils';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { classNames } from '../utils/UNSAFE_classNames';
import { useCollectionFocusRing } from '../hooks/PRIVATE_useCollectionFocusRing';
import { useCurrentKey } from '../hooks/PRIVATE_useCurrentKey';
import { Flex } from '../UNSAFE_Flex';
import { TabBarContext } from './TabBarContext';
import { useUser } from '../hooks/UNSAFE_useUser';
import { styles } from './themes/TabBarStyles.css';

/**
 * type for payload of current key change event handler
 */
export type CurrentKeyDetail<K> = {
  value: K;
};

/**
 * type for payload of selection change event handler
 */
type SelectionDetail<K> = {
  value: K;
};

export type TabBarProps<K extends string | number> = {
  /**
   * A set of TabBarItem(s) that TabBar will hold
   */
  children: ComponentChildren;
  /**
   * Whether to stretch the tab bar items to occupy available space or to condense items
   */
  layout?: 'stretch' | 'condense';
  /**
   * Key of the selected item.
   */
  selection?: K;
  /**
   * Callback function to handle when selection has changed
   */
  onSelectionChange?: <K extends string | number>(detail: SelectionDetail<K>) => void;
};

export function TabBar<K extends string | number>({
  children,
  layout = 'stretch',
  selection,
  onSelectionChange
}: TabBarProps<K>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const isFirstItemRendered = useRef<boolean>(false);
  const [currentKey, setCurrentKey] = useState<K | undefined>(selection);
  const [showFocusRing, focusRingProps] = useCollectionFocusRing(
    (elem: Element | null) => {
      return !!rootRef.current?.contains(elem);
    },
    ['ArrowRight', 'ArrowLeft']
  );

  const ITEM_SELECTOR = '.' + 'oj-c-tabbar-item';

  const { currentKeyProps } = useCurrentKey(
    (element) => keyExtractor(element, ITEM_SELECTOR),
    false,
    undefined,
    undefined,
    getPrevNextKey(rootRef.current, currentKey, true, ITEM_SELECTOR),
    getPrevNextKey(rootRef.current, currentKey, false, ITEM_SELECTOR),
    currentKey,
    (detail: CurrentKeyDetail<K | undefined>) => setCurrentKey(detail.value)
  );

  //If selection is undefined the currentKey would be undefined in that case keyboard
  //navigation using useCurrentKey is not possible as it expects a valid currentKey
  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (currentKey === undefined) {
        if (event.key === 'ArrowRight' || (!isRtl && event.key === 'ArrowLeft')) {
          const targetKey = (event.target as HTMLDivElement).getAttribute('data-oj-key');
          if (targetKey) {
            const nextKey = getPrevNextKey(rootRef.current, targetKey, false, ITEM_SELECTOR)();
            if (nextKey !== null) {
              setCurrentKey(nextKey as K);
            }
          }
        }
      }
    },
    [currentKey]
  );

  const onKeyDownProps = { onKeyDown };

  useEffect(() => {
    isFirstItemRendered.current = false;
  });

  const isActiveElement = useCallback(() => {
    return !!rootRef?.current?.contains(document.activeElement);
  }, []);

  const tabBarClass = classNames([styles.tabBarBase]);
  return (
    <TabBarContext.Provider
      value={{
        selection,
        onSelectionChange,
        currentKey,
        showFocusRing,
        layout,
        isActiveElement,
        isFirstItemRendered
      }}>
      <div
        class={tabBarClass}
        role={'tablist'}
        ref={rootRef}
        tabIndex={children ? -1 : 0}
        aria-multiselectable={false}
        {...mergeProps(onKeyDownProps, currentKeyProps, focusRingProps)}>
        <Flex direction="row" gap="8x">
          {children}
        </Flex>
      </div>
    </TabBarContext.Provider>
  );
}
